import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import RestaurantNavbar from '../Components/RestaurantNavbar';
import SalonPage from './Salon';
import AnfitrionPage from './Anfitrion';
import MeseroPage from './Mesero';
import CocinaPage from './Cocina';
import CajaPage from './Caja';
import DocumentModal from '../Components/DocumentModal';
import PaymentModal from '../Components/PaymentModal';
import ClienteFacturaModal from '../Components/ClienteFacturaModal';
import CajaModal from '../Components/CajaModal';
import useRestaurantStore from '../hooks/useRestaurantStore';
import useTavolataRouter from '../hooks/useTavolataRouter';
import { fetchCajaEstado, fetchCategories, fetchMetodosPago, fetchProductCache } from '../api/restaurantApi';
import { buildVentaPosPayload, crearVentaPos, imprimirDte } from '../api/ventasPos';
import { uuid } from '../utils/format';
import { TAVOLATA_ROUTES } from '../utils/restaurantPermissions';

function tenantId(tenant) {
  return tenant?.tenantid || tenant?.tenantId || tenant?.id || localStorage.getItem('tenantid') || 'default';
}

function productCacheKey(id) {
  return `integranube_restaurant_products:v1:${id}`;
}

function loadCachedProducts(id) {
  try {
    const parsed = JSON.parse(localStorage.getItem(productCacheKey(id)) || 'null');
    return Array.isArray(parsed?.products) ? parsed.products : [];
  } catch (_) {
    return [];
  }
}

function saveCachedProducts(id, products) {
  try {
    localStorage.setItem(productCacheKey(id), JSON.stringify({ updatedAt: new Date().toISOString(), products }));
  } catch (_) {}
}

function canUsePath(pathname, access) {
  if (pathname === TAVOLATA_ROUTES.SALON) return Boolean(access?.salon);
  if (pathname === TAVOLATA_ROUTES.ANFITRION) return Boolean(access?.anfitrion);
  if (pathname === TAVOLATA_ROUTES.MESERO) return Boolean(access?.mesero);
  if (pathname === TAVOLATA_ROUTES.COCINA) return Boolean(access?.cocina);
  if (pathname === TAVOLATA_ROUTES.CAJA) return Boolean(access?.caja);
  return false;
}

export default function Restaurant({ session, access, onLogout }) {
  const tid = tenantId(session?.tenant);
  const store = useRestaurantStore(tid);
  const router = useTavolataRouter();
  const [orderId, setOrderId] = useState(() => router.query.get('pedido'));
  const [products, setProducts] = useState(() => loadCachedProducts(tid));
  const [categories, setCategories] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(!products.length);
  const [cajaState, setCajaState] = useState({ abierta: false, caja: null });
  const [methods, setMethods] = useState([]);
  const [cajaOpen, setCajaOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [documento, setDocumento] = useState('boleta');
  const [cliente, setCliente] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const saleAttemptRef = useRef(null);

  const selectedOrder = useMemo(() => store.orders.find((order) => order.id === orderId) || null, [orderId, store.orders]);
  const notify = useCallback((message, severity = 'info') => setSnack({ open: true, message, severity }), []);

  useEffect(() => {
    const fromUrl = router.query.get('pedido');
    setOrderId(fromUrl || null);
  }, [router.pathname, router.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (router.pathname === '/' || !canUsePath(router.pathname, access)) {
      router.navigate(access?.defaultPath || '/', { replace: true });
    }
  }, [access, router.pathname, router.navigate]);

  const refreshCaja = useCallback(async () => {
    try {
      const state = await fetchCajaEstado();
      setCajaState(state);
      return state;
    } catch (err) {
      const closed = { abierta: false, caja: null };
      setCajaState(closed);
      return closed;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoadingCatalog(!products.length);
      const results = await Promise.allSettled([fetchCategories(), fetchProductCache(), fetchMetodosPago(), fetchCajaEstado()]);
      if (!alive) return;
      if (results[0].status === 'fulfilled') setCategories(results[0].value);
      if (results[1].status === 'fulfilled') {
        setProducts(results[1].value);
        saveCachedProducts(tid, results[1].value);
      }
      if (results[2].status === 'fulfilled') setMethods(results[2].value);
      if (results[3].status === 'fulfilled') setCajaState(results[3].value);
      setLoadingCatalog(false);
    };
    load();
    return () => { alive = false; };
  }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useCallback((path, options = {}) => router.navigate(path, options), [router.navigate]);

  const openTableForMesero = useCallback((table) => {
    if (!access?.mesero) return notify('Tu perfil no tiene permiso para gestionar pedidos.', 'warning');
    const existing = table?.existingOrderId || null;
    const id = existing || store.openTable(table);
    setOrderId(id);
    navigate(TAVOLATA_ROUTES.MESERO, { query: { pedido: id } });
  }, [access?.mesero, navigate, notify, store]);

  const openTakeaway = useCallback(() => {
    if (!access?.mesero) return notify('Tu perfil no tiene permiso para crear pedidos.', 'warning');
    const id = store.openTakeaway();
    setOrderId(id);
    navigate(TAVOLATA_ROUTES.MESERO, { query: { pedido: id } });
  }, [access?.mesero, navigate, notify, store]);

  const openFromSalon = useCallback((table) => {
    const active = store.activeOrders.find((order) => order.tableId === table?.id);
    if (access?.mesero) return openTableForMesero({ ...table, existingOrderId: active?.id });
    notify(active ? `${table.nombre}: ${active.status}.` : `${table.nombre} está disponible.`, 'info');
  }, [access?.mesero, notify, openTableForMesero, store.activeOrders]);

  const hostTable = useCallback((table) => {
    const active = store.activeOrders.find((order) => order.tableId === table?.id);
    if (active) notify(`${table.nombre} está ocupada · estado: ${active.status}.`, 'info');
    else notify(`${table.nombre} está libre y disponible.`, 'success');
  }, [notify, store.activeOrders]);

  const cashierPayment = (order) => {
    if (!order?.items?.length) return notify('El pedido está vacío.', 'warning');
    setOrderId(order.id);
    if (!cajaState.abierta) {
      notify('Debe abrir caja antes de cobrar.', 'warning');
      setCajaOpen(true);
      return;
    }
    setCliente(null);
    setDocumento('boleta');
    setDocumentOpen(true);
  };

  const requestPayment = () => {
    if (!access?.caja) return notify('El cobro debe realizarlo un usuario con permiso de caja.', 'warning');
    if (!selectedOrder?.items?.length) return notify('El pedido está vacío.', 'warning');
    if (!cajaState.abierta) {
      notify('Debe abrir caja antes de cobrar.', 'warning');
      setCajaOpen(true);
      return;
    }
    setCliente(null);
    setDocumento('boleta');
    setDocumentOpen(true);
  };

  const chooseDocument = (type) => {
    setDocumento(type);
    setDocumentOpen(false);
    if (type === 'factura') setCustomerOpen(true);
    else setPaymentOpen(true);
  };

  const chooseCustomer = (customer) => {
    setCliente(customer);
    setCustomerOpen(false);
    setPaymentOpen(true);
  };

  const confirmPayment = async (payments) => {
    if (!selectedOrder || processing) return;
    if (!cajaState.abierta || !cajaState.caja?.id) throw new Error('No hay caja abierta.');
    if (documento === 'factura' && !cliente?.id) throw new Error('Seleccione un cliente para Factura.');

    const signature = JSON.stringify({
      orderId: selectedOrder.id,
      updatedAt: selectedOrder.updatedAt,
      documento,
      clienteId: cliente?.id || null,
      total: store.orderTotal(selectedOrder),
    });

    if (!saleAttemptRef.current || saleAttemptRef.current.signature !== signature) {
      saleAttemptRef.current = { signature, clientSaleId: uuid() };
    }

    setProcessing(true);
    try {
      const payload = buildVentaPosPayload({
        order: selectedOrder,
        payments,
        documento,
        cliente,
        cajaId: cajaState.caja.id,
        clientSaleId: saleAttemptRef.current.clientSaleId,
      });
      const result = await crearVentaPos(payload);

      let printError = null;
      try {
        await imprimirDte(result, documento);
      } catch (err) {
        printError = err;
      }

      store.markPaid(selectedOrder.id, result);
      saleAttemptRef.current = null;
      setPaymentOpen(false);
      setOrderId(null);
      setCliente(null);
      setDocumento('boleta');
      await refreshCaja();
      navigate(access?.caja ? TAVOLATA_ROUTES.CAJA : access?.defaultPath || '/', { replace: true });

      const saleId = result?.venta?.id_venta;
      notify(
        `Venta${saleId ? ` #${saleId}` : ''} registrada${printError ? ` · DTE emitido, pero no se pudo imprimir: ${printError.message}` : ' · impresión enviada'}.`,
        printError ? 'warning' : 'success'
      );
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo registrar la venta.';
      notify(message, 'error');
      throw new Error(message);
    } finally {
      setProcessing(false);
    }
  };

  const renderPage = () => {
    if (router.pathname === TAVOLATA_ROUTES.SALON && access?.salon) {
      return <SalonPage store={store} onOpenTable={openFromSalon} onTakeaway={access?.mesero ? openTakeaway : undefined} />;
    }
    if (router.pathname === TAVOLATA_ROUTES.ANFITRION && access?.anfitrion) {
      return <AnfitrionPage store={store} onOpenTable={hostTable} />;
    }
    if (router.pathname === TAVOLATA_ROUTES.MESERO && access?.mesero) {
      return (
        <MeseroPage
          store={store}
          order={selectedOrder}
          products={products}
          categories={categories}
          loadingCatalog={loadingCatalog}
          onOpenTable={openTableForMesero}
          onTakeaway={openTakeaway}
          onBack={() => { setOrderId(null); navigate(TAVOLATA_ROUTES.MESERO, { replace: true }); }}
          onSendKitchen={() => {
            if (!selectedOrder) return;
            store.sendKitchen(selectedOrder.id);
            notify('Comanda enviada a cocina.', 'success');
          }}
          onPay={access?.caja ? requestPayment : undefined}
        />
      );
    }
    if (router.pathname === TAVOLATA_ROUTES.COCINA && access?.cocina) {
      return <CocinaPage store={store} />;
    }
    if (router.pathname === TAVOLATA_ROUTES.CAJA && access?.caja) {
      return <CajaPage store={store} cajaState={cajaState} onOpenCaja={() => setCajaOpen(true)} onSelectOrder={cashierPayment} />;
    }
    return <Box sx={{ p: 3 }}><Alert severity="info">Redirigiendo a una vista permitida…</Alert></Box>;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#edf3f8' }}>
      <RestaurantNavbar
        user={session?.user}
        tenant={session?.tenant}
        pathname={router.pathname}
        onNavigate={(path) => navigate(path)}
        caja={cajaState}
        onCaja={() => setCajaOpen(true)}
        onLogout={onLogout}
        access={access}
      />

      <Box sx={{ pt: '82px', minHeight: '100vh' }}>{renderPage()}</Box>

      <CajaModal open={cajaOpen} onClose={() => setCajaOpen(false)} cajaState={cajaState} onRefresh={refreshCaja} />
      <DocumentModal open={documentOpen} onClose={() => setDocumentOpen(false)} onChoose={chooseDocument} />
      <ClienteFacturaModal open={customerOpen} onClose={() => { setCustomerOpen(false); setDocumentOpen(true); }} onSelect={chooseCustomer} />
      <PaymentModal open={paymentOpen} total={selectedOrder ? store.orderTotal(selectedOrder) : 0} methods={methods} onClose={() => setPaymentOpen(false)} onConfirm={confirmPayment} processing={processing} />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((current) => ({ ...current, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
